import {
  App as CapacitorApp,
  type PluginListenerHandle,
} from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { storage } from '@/plugins/ionic-storage';
import { onBeforeUnmount, onMounted } from 'vue';
import { useRouter } from 'vue-router';

type UsePageHideOptions = {
  inactiveLimit?: number;
  homePath?: string;
  storageKey?: string;
};

const DEFAULT_INACTIVE_LIMIT = 1 * 60 * 1000; // 1分钟
const DEFAULT_HOME_PATH = '/grass';
const DEFAULT_STORAGE_KEY = 'appInactiveAt';

/**
 * 超时回到首页：进入后台、熄屏或关闭网页超过指定时间后，
 * 再次进入时回到首页并完整刷新应用。
 */
export default function usePageHide(options: UsePageHideOptions = {}) {
  const router = useRouter();
  const inactiveLimit =
    options.inactiveLimit ?? DEFAULT_INACTIVE_LIMIT;
  const homePath = options.homePath ?? DEFAULT_HOME_PATH;
  const storageKey = options.storageKey ?? DEFAULT_STORAGE_KEY;

  let appStateListener: PluginListenerHandle | null = null;
  let disposed = false;
  let isReloadingToHome = false;
  let checkPromise: Promise<boolean> | null = null;
  let recordPromise: Promise<unknown> | null = null;

  const recordInactiveTime = async () => {
    if (isReloadingToHome) return;

    const currentRecordPromise = storage.set(storageKey, Date.now());
    recordPromise = currentRecordPromise;

    try {
      await currentRecordPromise;
    } finally {
      if (recordPromise === currentRecordPromise) {
        recordPromise = null;
      }
    }
  };

  const checkInactiveTime = (): Promise<boolean> => {
    if (checkPromise) return checkPromise;

    checkPromise = (async () => {
      // 熄屏后很快恢复时，先等待后台时间写入完成。
      if (recordPromise) await recordPromise;

      const value = await storage.get(storageKey);

      // 一次后台记录只判断一次，防止恢复后继续累计时间。
      await storage.remove(storageKey);

      if (!value) return false;

      const inactiveAt = Number(value);
      if (!Number.isFinite(inactiveAt)) return false;
      if (Date.now() - inactiveAt <= inactiveLimit) return false;

      isReloadingToHome = true;
      const homeUrl = router.resolve(homePath).href;
      window.location.replace(homeUrl);
      return true;
    })().finally(() => {
      checkPromise = null;
    });

    return checkPromise;
  };

  const safelyRecordInactiveTime = () => {
    void recordInactiveTime().catch((error) => {
      console.warn('记录应用离开时间失败', error);
    });
  };

  const safelyCheckInactiveTime = () => {
    void checkInactiveTime().catch((error) => {
      console.warn('检查应用离开时间失败', error);
    });
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      safelyRecordInactiveTime();
    } else if (document.visibilityState === 'visible') {
      safelyCheckInactiveTime();
    }
  };

  const handlePageHide = () => {
    safelyRecordInactiveTime();
  };

  onMounted(async () => {
    let shouldReload = false;
    try {
      shouldReload = await checkInactiveTime();
    } catch (error) {
      console.warn('初始化应用离开时间检查失败', error);
    }

    if (shouldReload || disposed) return;

    window.addEventListener('pagehide', handlePageHide);

    if (Capacitor.getPlatform() === 'web') {
      document.addEventListener(
        'visibilitychange',
        handleVisibilityChange,
      );
      return;
    }

    appStateListener = await CapacitorApp.addListener(
      'appStateChange',
      ({ isActive }) => {
        if (isActive) {
          safelyCheckInactiveTime();
        } else {
          safelyRecordInactiveTime();
        }
      },
    );

    if (disposed) {
      await appStateListener.remove();
      appStateListener = null;
    }
  });

  onBeforeUnmount(() => {
    disposed = true;
    window.removeEventListener('pagehide', handlePageHide);
    document.removeEventListener(
      'visibilitychange',
      handleVisibilityChange,
    );

    if (appStateListener) {
      void appStateListener.remove();
      appStateListener = null;
    }
  });

  return {
    recordInactiveTime,
    checkInactiveTime,
  };
}
